import { Button, Card, CardContent, CardHeader, CardTitle, cn, Separator } from '@1trading/react';
import { CircleCheck, CircleX, Pencil } from 'lucide-react';
import { useState } from 'react';
import { BROKER_SETTING_OPTIONS } from '@/features/brokers/list/broker-options';
import type { WireBroker } from '@/services/api/brokers';
import { EditSettingsDialog } from './edit-settings-dialog';

const SettingRow = ({ title, isEnabled }: { title: string; isEnabled: boolean }) => (
  <li className="flex items-center gap-2">
    {isEnabled ? <CircleCheck aria-hidden className="size-4 shrink-0 text-emerald-500" /> : <CircleX aria-hidden className="size-4 shrink-0 text-muted-foreground/60" />}
    <span className={cn('text-sm', !isEnabled && 'text-muted-foreground')}>{title}</span>
  </li>
);
SettingRow.displayName = 'SettingRow';

const FeeSplitRow = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-center justify-between gap-4 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium">{value}</span>
  </div>
);
FeeSplitRow.displayName = 'FeeSplitRow';

const NOT_CONFIGURED = 'Not configured';

const percentOrNotConfigured = (value: number | undefined): string => (value === undefined ? NOT_CONFIGURED : `${value}%`);

interface OverviewTabProps {
  broker: WireBroker;
}

export const OverviewTab = ({ broker }: OverviewTabProps) => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const feeSplit = broker.fee_split;
  // Defensive ?? [] — the lambda always emits `settings`, but very old cached payloads may omit it
  const enabledSettings = broker.settings ?? [];

  return (
    <Card className="max-w-[800px]">
      <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
        <CardTitle>Broker Settings</CardTitle>
        <Button className="gap-1.5" onClick={() => setIsEditOpen(true)} size="sm" type="button">
          <Pencil className="size-4" />
          Edit Settings
        </Button>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {/* Always lists ALL 5 settings in the fixed spec order */}
        <ul className="flex flex-col gap-2">
          {BROKER_SETTING_OPTIONS.map((option) => (
            <SettingRow isEnabled={enabledSettings.includes(option.value)} key={option.value} title={option.title} />
          ))}
        </ul>

        <Separator />

        <div className="flex flex-col gap-2">
          {!feeSplit ? (
            <FeeSplitRow label="Fee Split" value={NOT_CONFIGURED} />
          ) : feeSplit.type === 'percentage' ? (
            <>
              <FeeSplitRow label="Fee Split Type" value="Percentage" />
              <FeeSplitRow label="Trades" value={percentOrNotConfigured(feeSplit.trades)} />
              <FeeSplitRow label="Liquidations" value={percentOrNotConfigured(feeSplit.liquidations)} />
            </>
          ) : (
            <>
              <FeeSplitRow label="Fee Split Type" value="Basis Points" />
              <FeeSplitRow label="OT Maker Fee" value={`${feeSplit.maker_fee_bps} bps`} />
              <FeeSplitRow label="OT Taker Fee" value={`${feeSplit.taker_fee_bps} bps`} />
              <FeeSplitRow label="Liquidations" value={percentOrNotConfigured(feeSplit.liquidations)} />
            </>
          )}
        </div>
      </CardContent>

      {/* Mounted per open so the form prefills from the FRESHEST broker data every
          time — the PUT is full-state replace, so a stale prefill would silently
          strip settings another operator just added. */}
      {isEditOpen ? <EditSettingsDialog broker={broker} onClose={() => setIsEditOpen(false)} /> : null}
    </Card>
  );
};
OverviewTab.displayName = 'OverviewTab';
